/**
 * Lightbox נגיש — TASKS 4.2, PRD 8.1: "role='dialog' · aria-modal · מלכודת
 * פוקוס · Esc · החזרת פוקוס". ⛔ **מימוש עצמי, לא ספרייה חיצונית** (PRD 8.1
 * — "לא ספרייה חיצונית כבדה").
 *
 * ⚠️ ניווט בין תמונות (הבא/הקודם) הוא תוסף מעבר לדרישה המילולית — נחוץ בפועל
 *    כי SH-0001 מציג 5 תמונות ואריאנט, ולקוח שפתח Lightbox על אחת מהן ירצה
 *    לדפדף בלי לסגור ולפתוח מחדש.
 */
import { useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { CaretLeft, CaretRight, X } from '@phosphor-icons/react';

export interface LightboxImage {
  url: string;
  alt: string;
}

interface LightboxProps {
  images: readonly LightboxImage[];
  index: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function Lightbox({ images, index, onClose, onNavigate }: LightboxProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const current = images[index];

  // ⭐ פוקוס נכנס לדיאלוג בפתיחה, וחוזר לאלמנט שהפעיל אותו בסגירה
  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => {
      previouslyFocused.current?.focus();
    };
    // ⚠️ תלוי בכוונה רק בעליית/ירידת הרכיב — לא ב-index/images, אחרת הפוקוס
    //    היה קופץ בכל דפדוף בין תמונות
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      // ⚠️ Left = הקודם, Right = הבא — לפי סמנטיקת המקש (WAI-ARIA carousel
      //    pattern), ⛔ לא לפי כיוון חזותי. עקבי בין RTL ל-LTR.
      if (event.key === 'ArrowLeft' && images.length > 1) {
        onNavigate((index - 1 + images.length) % images.length);
        return;
      }
      if (event.key === 'ArrowRight' && images.length > 1) {
        onNavigate((index + 1) % images.length);
        return;
      }
      if (event.key === 'Tab') {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
        if (focusable.length === 0) return;
        const first = focusable[0] as HTMLElement;
        const last = focusable[focusable.length - 1] as HTMLElement;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [images.length, index, onClose, onNavigate]);

  if (!current) return null;

  return (
    <div
      className="lightbox-overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={current.alt || 'הגדלת תמונת מוצר'}
        className="lightbox-dialog"
        tabIndex={-1}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.15 }}
      >
        <button type="button" className="lightbox-close" onClick={onClose} aria-label="סגירה">
          <X size={20} weight="regular" aria-hidden="true" />
        </button>

        <img src={current.url} alt={current.alt} className="lightbox-image" />

        {images.length > 1 && (
          <div className="lightbox-nav">
            {/* ⚠️ הכיווניות הפוכה מ-LTR בכוונה — בעמוד RTL "הקודם" מצביע ימינה */}
            <button
              type="button"
              className="lightbox-nav-btn"
              onClick={() => onNavigate((index - 1 + images.length) % images.length)}
              aria-label="תמונה קודמת"
            >
              <CaretRight size={20} weight="regular" aria-hidden="true" />
            </button>
            <span className="sr-only" aria-live="polite">
              תמונה {index + 1} מתוך {images.length}
            </span>
            <button
              type="button"
              className="lightbox-nav-btn"
              onClick={() => onNavigate((index + 1) % images.length)}
              aria-label="תמונה הבאה"
            >
              <CaretLeft size={20} weight="regular" aria-hidden="true" />
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
